import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight } from "lucide-react";
import { IMAGES, TIMELINE, PROFILO_GRUPPO } from "@/data/content";
import { Reveal, GoldLine, SectionHeading } from "@/components/Reveal";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <section ref={ref} data-testid="chi-siamo-hero" className="relative h-[70vh] min-h-[480px] overflow-hidden">
      <motion.div style={{ y: imgY }} className="absolute inset-0">
        <img src={IMAGES.chiSiamoHero} alt="" className="w-full h-full object-cover scale-105" />
      </motion.div>
      <div className="absolute inset-0 bg-[#080808]/75" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#080808]/50 via-transparent to-[#080808]" />
      <div className="relative z-10 h-full max-w-7xl mx-auto px-6 lg:px-10 flex flex-col justify-end pb-20">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="eyebrow mb-6"
        >
          Chi Siamo
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="font-display text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight text-[#F5F2EB]"
        >
          La storia del <span className="gold-gradient-text italic">Gruppo.</span>
        </motion.h1>
        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 1.2, delay: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="gold-line origin-left w-40 mt-8"
        />
      </div>
    </section>
  );
};

const Editoriale = () => (
  <section data-testid="chi-siamo-editoriale" className="bg-[#080808] py-24 lg:py-32">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
      <div>
        <SectionHeading eyebrow={PROFILO_GRUPPO.eyebrow} title="Vision. Strategy." goldWord="Value." />
        <Reveal delay={0.2}>
          <p className="mt-10 text-lg font-light text-[#A5A5A5] leading-relaxed">
            {PROFILO_GRUPPO.testo}
          </p>
        </Reveal>
        <Reveal delay={0.3}>
          <p className="mt-6 text-lg font-light text-[#A5A5A5] leading-relaxed">
            {PROFILO_GRUPPO.testo2}
          </p>
        </Reveal>
        <Reveal delay={0.4}>
          <p className="mt-8 font-display text-lg italic gold-gradient-text">
            {PROFILO_GRUPPO.claim}
          </p>
        </Reveal>
      </div>
      <Reveal delay={0.2} className="relative">
        <div className="absolute -inset-4 border border-[#C6A45D]/30 pointer-events-none" />
        <img
          src={IMAGES.heroHome}
          alt="Billions Group"
          loading="lazy"
          className="w-full h-[420px] lg:h-[520px] object-cover brightness-[0.7]"
        />
      </Reveal>
    </div>
  </section>
);

const Posizionamento = () => (
  <section data-testid="chi-siamo-posizionamento" className="bg-[#080808] py-24 lg:py-28">
    <div className="max-w-4xl mx-auto px-6 text-center">
      <Reveal>
        <p className="eyebrow mb-6">Posizionamento del Gruppo</p>
      </Reveal>
      <Reveal delay={0.1}>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light text-[#F5F2EB] tracking-tight">
          Un'unica visione, <span className="gold-gradient-text italic">sei aree di sviluppo.</span>
        </h2>
      </Reveal>
      <GoldLine className="mt-10 w-28 mx-auto" />
      <Reveal delay={0.2}>
        <p className="mt-8 text-lg font-light text-[#A5A5A5] leading-relaxed">
          {PROFILO_GRUPPO.posizionamento}
        </p>
      </Reveal>
    </div>
  </section>
);

const TimelineSection = () => (
  <section data-testid="chi-siamo-timeline" className="bg-[#141414] py-24 lg:py-32">
    <div className="max-w-5xl mx-auto px-6 lg:px-10">
      <SectionHeading eyebrow="Percorso" title="La nostra" goldWord="evoluzione." />
      <div className="mt-20 relative">
        <motion.div
          initial={{ scaleY: 0 }}
          whileInView={{ scaleY: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
          className="absolute left-[7px] sm:left-1/2 top-0 bottom-0 w-px origin-top bg-gradient-to-b from-[#8F6B32] via-[#C6A45D] to-[#E5C776]"
        />
        <div className="space-y-16">
          {TIMELINE.map((t, i) => (
            <Reveal key={t.anno} delay={0.1}>
              <div
                data-testid={`timeline-item-${i}`}
                className={`relative flex flex-col sm:flex-row gap-6 sm:gap-0 ${
                  i % 2 === 0 ? "sm:flex-row" : "sm:flex-row-reverse"
                }`}
              >
                <div className="absolute left-0 sm:left-1/2 sm:-translate-x-1/2 top-1 w-[15px] h-[15px] rotate-45 border border-[#C6A45D] bg-[#141414]" />
                <div className={`sm:w-1/2 ${i % 2 === 0 ? "sm:pr-14 sm:text-right" : "sm:pl-14"} pl-10 sm:pl-0 ${i % 2 !== 0 ? "sm:pl-14" : ""}`}>
                  <p className="eyebrow mb-2">{t.anno}</p>
                  <h3 className="font-display text-2xl sm:text-3xl font-light text-[#F5F2EB]">{t.titolo}</h3>
                  <p className="mt-3 text-sm text-[#A5A5A5] leading-relaxed font-light">{t.testo}</p>
                </div>
                <div className="hidden sm:block sm:w-1/2" />
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const Organigramma = () => (
  <section data-testid="chi-siamo-organigramma" className="bg-[#080808] py-24 lg:py-32">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <SectionHeading eyebrow="Struttura" title="L'organizzazione del" goldWord="Gruppo." align="center" />
      <Reveal delay={0.2}>
        <div className="mt-16 relative max-w-5xl mx-auto">
          <div className="absolute -inset-3 sm:-inset-4 border border-[#C6A45D]/30 pointer-events-none" />
          <img
            src="/assets/organigramma.jpeg"
            alt="Organigramma Billions Group — Direzione Generale, Staff Partnership e Sviluppo, Business Unit: Food, Luxury, Beauty, Real Estate, Hospitality, New Venture"
            data-testid="organigramma-image"
            className="w-full h-auto"
          />
        </div>
      </Reveal>
      <Reveal delay={0.3}>
        <p className="mt-12 text-center text-sm text-[#A5A5A5] font-light max-w-2xl mx-auto leading-relaxed">
          La Direzione Generale coordina le sei business unit del Gruppo,
          supportata dallo staff Partnership e Sviluppo per le relazioni B2B e
          le nuove opportunità.
        </p>
      </Reveal>
    </div>
  </section>
);

const Cta = () => (
  <section className="bg-[#141414] py-24">
    <div className="max-w-3xl mx-auto px-6 text-center">
      <Reveal>
        <h2 className="font-display text-3xl sm:text-4xl font-light text-[#F5F2EB]">
          Entrate in contatto con il <span className="gold-gradient-text italic">Gruppo.</span>
        </h2>
      </Reveal>
      <GoldLine className="mt-8 w-24 mx-auto" />
      <Reveal delay={0.2}>
        <Link
          to="/contatti"
          data-testid="chi-siamo-cta-contatti"
          className="group mt-10 inline-flex items-center gap-3 px-10 py-4 bg-[#C6A45D] text-[#080808] text-xs tracking-[0.25em] uppercase font-semibold hover:bg-[#E5C776] transition-colors duration-300"
        >
          Contattaci
          <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </Reveal>
    </div>
  </section>
);

export default function ChiSiamo() {
  return (
    <main data-testid="chi-siamo-page">
      <Hero />
      <Editoriale />
      <TimelineSection />
      <Organigramma />
      <Posizionamento />
      <Cta />
    </main>
  );
}
